import { Link } from "react-router-dom";

export default function HowToGetOutOfDebt() {
    return (
        <>
            <p>
                Getting out of debt is less about finding a trick and more about choosing a strategy, sticking to it,
                and understanding the math behind it. The right approach depends on your debt types, interest rates,
                income, and psychology. This guide covers the two proven payoff methods, how to stop accumulating more
                debt while paying it down, and what to do once you're free.
            </p>

            <h2>Step 1: Know Exactly What You Owe</h2>
            <p>
                Before you can build a payoff plan, you need a complete picture. List every debt you carry:
            </p>
            <p>
                Creditor name | Balance | Interest rate (APR) | Minimum payment | Due date
            </p>
            <p>
                Include everything — credit cards, personal loans, auto loans, student loans, medical debt, buy-now
                pay-later balances. Most people are surprised by the total when they see it written out. That clarity
                is the starting point.
            </p>
            <p>
                Once you have the list, sort it two ways: by interest rate (highest to lowest) and by balance
                (smallest to largest). You'll use one of these sorted lists depending on which payoff method you
                choose.
            </p>

            <h2>The Two Payoff Methods</h2>
            <p>
                <strong>Debt Avalanche (highest rate first):</strong> Pay minimums on everything, then throw every
                extra dollar at the highest-interest debt. When it's gone, roll that payment into the next highest.
                This method minimizes total interest paid — it's the mathematically optimal approach.
            </p>
            <p>
                <strong>Debt Snowball (smallest balance first):</strong> Pay minimums on everything, then attack the
                smallest balance regardless of rate. When it's gone, roll that payment into the next smallest. This
                method maximizes psychological momentum — you get wins faster, which keeps many people on track longer.
            </p>
            <p>
                Research consistently shows the snowball method leads to higher completion rates for people who have
                struggled with consistency. The avalanche saves more money on paper, but only if you actually follow
                through. Choose the one you'll stick with.
            </p>
            <p>
                Use the <Link to="/calculator/debt-payoff">debt payoff calculator</Link> to model both methods with
                your actual balances and rates to see the difference in time and total interest paid.
            </p>

            <h2>Step 2: Free Up Cash to Attack the Debt</h2>
            <p>
                The size of your extra payment determines how fast you get out. Two levers: reduce expenses or increase
                income.
            </p>
            <p>
                <strong>Reduce expenses:</strong> Audit subscriptions, negotiate bills (insurance, phone, internet),
                cut discretionary spending temporarily. You don't need to do this forever — just long enough to build
                momentum. Even an extra $100/month makes a significant difference in payoff timeline.
            </p>
            <p>
                <strong>Increase income:</strong> A side income stream directed entirely at debt accelerates the
                timeline dramatically. Freelance work, overtime, selling unused items, gig economy work — any of it
                goes straight to the target debt. An extra $500/month on a $10,000 balance at 20% APR cuts payoff
                time from over 4 years (minimums only) to under 2 years.
            </p>
            <p>
                <strong>Use windfalls:</strong> Tax refunds, bonuses, and cash gifts go directly to the debt. This
                isn't optional if you're serious about the timeline. A $2,000 tax refund applied to a 24% APR credit
                card is the equivalent of a guaranteed 24% return on that money.
            </p>

            <h2>Step 3: Stop Adding to the Debt</h2>
            <p>
                Paying down debt while continuing to add new charges is running on a treadmill. The strategies below
                don't require cutting up cards — they require changing habits:
            </p>
            <p>
                <strong>Switch to cash or debit for variable spending.</strong> When money is physically leaving your
                hand or immediately leaving your account, spending behavior changes.
            </p>
            <p>
                <strong>Build a small emergency fund first.</strong> Counterintuitively, having $1,000–$2,000 in
                savings before aggressively paying debt prevents new debt from being created when unexpected expenses
                hit. Without it, every car repair or medical copay goes back on a credit card.
            </p>
            <p>
                <strong>Identify the spending triggers.</strong> Most credit card debt accumulates in specific
                categories — restaurants, online shopping, subscriptions. Tracking for 30 days usually reveals the
                pattern quickly.
            </p>

            <h2>Balance Transfer and Consolidation Options</h2>
            <p>
                <strong>Balance transfer cards:</strong> Some cards offer 0% APR for 12–21 months on transferred
                balances, often with a 3%–5% transfer fee. If you can pay off the balance before the promotional
                period ends, this can save hundreds in interest. The risk: if you don't pay it off in time, you may
                owe deferred interest depending on the card terms.
            </p>
            <p>
                <strong>Personal loan consolidation:</strong> Rolling multiple high-rate debts into a single
                lower-rate personal loan simplifies payments and can reduce total interest. This works if your credit
                score qualifies you for a meaningfully lower rate — typically below 15%. Rates above that rarely
                make consolidation worthwhile.
            </p>
            <p>
                <strong>Home equity:</strong> A HELOC or cash-out refinance can consolidate debt at mortgage rates,
                but converts unsecured debt to secured debt. If you default, your home is at risk. Use this option
                only if you've addressed the underlying spending behavior and have a clear payoff plan. See the{" "}
                <Link to="/calculator/heloc">HELOC calculator</Link> for a comparison.
            </p>

            <h2>What to Do When You're Debt-Free</h2>
            <p>
                The payment you were making toward debt doesn't disappear — it redirects. This is the inflection
                point where wealth starts building:
            </p>
            <p>
                1. Build a full 3–6 month emergency fund so you never need to go back into high-rate debt for
                unexpected expenses.<br />
                2. Max out tax-advantaged retirement accounts (401k to employer match first, then IRA, then back
                to max 401k).<br />
                3. Invest the remainder in a taxable brokerage account or toward other financial goals.
            </p>
            <p>
                The same discipline that got you out of debt — directing money intentionally rather than spending
                whatever's left — is what builds the next phase.
            </p>

            <h2>The Bottom Line</h2>
            <p>
                Getting out of debt requires a written plan, a chosen method (avalanche or snowball), extra cash
                directed at the target, and no new accumulation. The math is straightforward. The hard part is
                the consistency. Pick a method, automate your minimum payments, and manually apply every extra
                dollar to the target debt on payday. Use the{" "}
                <Link to="/calculator/debt-payoff">debt payoff calculator</Link> to set a specific payoff date —
                having a date makes it real.
            </p>
        </>
    );
}
